import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18n-js';
import useStyles from './Services.styled';
import { uiActions } from 'state/modules/UI';
import { SelectableList } from 'components/UI';
import { useDispatch, useSelector } from 'react-redux';
import { VehiclesGroupsActions } from 'state/modules/vehiclesGroups';
import { Box, Grid, Button, Container, Typography } from '@material-ui/core';
import { FETCH_STATE } from 'constants';
import { useConfirm } from 'hooks';
import { UnshareDisclaimer } from '../../../VehiclesGroups';

const VehiclesGroupsServices = ({ fleet, customErrors }) => {
  const { id: fleetId } = fleet;
  const classes = useStyles();
  const dispatch = useDispatch();
  const [refetch, setRefetch] = useState(false);
  const [selectedAssignedRowId, setSelectedAssignedRowId] = useState(null);
  const [selectedAvailableRowId, setSelectedAvailableRowId] = useState(null);
  const [onUnshareBatch, setOnUnshareBatch] = useState([]);
  const availableServices = useSelector((state) => state.vehiclesGroups.availableServices);
  const fetchAvailableServices = useSelector(
    (state) => state.vehiclesGroups.fetchAvailableServices
  );
  const sharedServices = useSelector((state) => state.vehiclesGroups.sharedServices);
  const fetchSharedServices = useSelector((state) => state.vehiclesGroups.fetchSharedFleets);
  const fetchUpdateFleets = useSelector((state) => state.vehiclesGroups.fetchUpdateFleets);
  const sharingType = useSelector(
    (state) => state.config?.settings?.core?.partnership?.sharing_type
  );
  // Enable pagination for fleets list
  // const availableServicesPagination = useSelector(
  //   (state) => state.vehiclesGroups.availableServicesPagination
  // );
  // const sharedServicesPagination = useSelector(
  //   (state) => state.vehiclesGroups.sharedServicesPagination
  // );
  const orgGracePeriod = useSelector(
    (state) => state.config?.settings?.core?.organization?.share_grace_period
  );

  const loading =
    fetchAvailableServices === FETCH_STATE.LOADING || fetchSharedServices === FETCH_STATE.LOADING;
  const updating = fetchUpdateFleets === FETCH_STATE.LOADING;

  const availableServicesColumns = [
    {
      name: i18n.t('common.name'),
      selector: 'name',
      sortable: false,
    },
  ];

  const assignedServicesColumns = [
    {
      name: i18n.t('common.name'),
      selector: 'name',
      sortable: false,
    },
  ];

  const addServiceToVehicleGroup = () => {
    if (!selectedAvailableRowId) {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: i18n.t('vehicle_groups.select_service'),
        })
      );
    }
    const payload = {
      organization_id: selectedAvailableRowId,
    };
    setSelectedAvailableRowId(null);
    setOnUnshareBatch((prevState) => [...prevState].filter((id) => id !== selectedAvailableRowId));
    dispatch(VehiclesGroupsActions.shareFleet(fleetId, payload));
  };

  const removeServiceFromVehicleGroup = () => {
    const payload = {
      organization_id: selectedAssignedRowId,
    };
    setSelectedAssignedRowId(null);
    setOnUnshareBatch((prevState) => [...prevState, selectedAssignedRowId]);
    dispatch(VehiclesGroupsActions.unshareFleet(fleetId, payload));
  };

  //Load available services on mount
  useEffect(() => {
    dispatch(
      VehiclesGroupsActions.fetchAvailableServices({
        status: ['ACTIVE', 'CANREQUESTED'],
      })
    );
  }, [dispatch]);

  useEffect(() => {
      setRefetch(fetchUpdateFleets === FETCH_STATE.DONE);
  }, [fetchUpdateFleets]);

  //Load shared services on mount
  useEffect(() => {
    if (fleetId) {
      dispatch(
        VehiclesGroupsActions.fetchSharedServices({
          status: ['ACTIVE', 'CANREQUESTED'],
          vehicle_group_id: fleetId,
        })
      );
    }
  }, [fleetId]);

  //Reload shared and fleet on update
  useEffect(() => {
    if (refetch && fleetId) {
      dispatch(
        VehiclesGroupsActions.fetchSharedServices({
          status: ['ACTIVE', 'CANREQUESTED'],
          vehicle_group_id: fleetId,
        })
      );
      dispatch(VehiclesGroupsActions.fetchVehicleGroupById(fleetId, null));
    }
  }, [dispatch, fleetId, refetch]);

  const updatedServices = useMemo(
    () =>
      availableServices?.map((availableService) => ({
        ...availableService,
        disabled: !!sharedServices?.find(
          (sharedService) => sharedService.id === availableService.id
        ),
      })) || [],
    [availableServices, sharedServices]
  );

  const removeServices = useMemo(
    () =>
      sharedServices?.map((sharedService) => ({
        ...sharedService,
        disabled: !!onUnshareBatch?.find((serviceId) => serviceId === sharedService.id),
      })) || [],
    [sharedServices, onUnshareBatch]
  );

  const onAvailableRowClicked = useCallback(
    (row) => {
      setSelectedAvailableRowId(row?.id);
    },
    [setSelectedAvailableRowId]
  );

  const onAssignedRowClicked = useCallback(
    (row) => {
      setSelectedAssignedRowId(row?.id);
    },
    [setSelectedAssignedRowId]
  );

  const gracePeriod =
    removeServices.find((service) => service.id === selectedAssignedRowId)?.relationship
      .share_grace_period ||
    orgGracePeriod ||
    0;

  const [toggleRemoveService, RemoveServiceConfirm] = useConfirm({
    title: i18n.t('vehicle.bulk_unshare_confirm_title'),
    message: <UnshareDisclaimer gracePeriod={gracePeriod} />,
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.yes'),
    onConfirm: removeServiceFromVehicleGroup,
    additionalProps: { modalProps: { maxWidth: 450, containerCustomClass: classes.confirmHeader } },
  });

  return (
    <Box className={classes.container}>
      <RemoveServiceConfirm />
      <Container className="h-100" disableGutters={true}>
        <Grid container direction="row" justifyContent="center" alignItems="center">
          <Grid item xs={12}>
            <Typography gutterBottom component="div" align="center">
              <Box data-cy="step-title" fontWeight="fontWeightBold" fontSize={14} my={2}>
                {sharingType === 'FFB'
                  ? i18n.t('vehicle_groups.grant_access_to_partners')
                  : i18n.t('vehicle_groups.grant_access_to_services')}
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Grid container direction="row" justifyContent="space-around" alignItems="flex-start">
              <Grid item xs={5}>
                <SelectableList
                  mainTable={true}
                  pagination={false}
                  dataCy="available-service-list"
                  columns={availableServicesColumns}
                  loading={loading}
                  data={updatedServices}
                  onRowClicked={onAvailableRowClicked}
                  title={i18n.t('vehicle_groups.available_services')}
                />
              </Grid>
              <Grid item xs={2}>
                <Box
                  display="flex"
                  alignItems="center"
                  flexDirection="column"
                  justifyContent="center"
                >
                  <Box py={1} px={2} style={{ width: '100%' }}>
                    <Button
                      onClick={addServiceToVehicleGroup}
                      disabled={loading || updating || !selectedAvailableRowId}
                      variant="outlined"
                      style={{
                        textTransform: 'capitalize',
                      }}
                      color="primary"
                      fullWidth
                      data-cy="add-service-btn"
                    >
                      {i18n.t('common.add')}
                    </Button>
                  </Box>
                  <Box py={1} px={2} style={{ width: '100%' }}>
                    <Button
                      onClick={toggleRemoveService}
                      disabled={loading || updating || !selectedAssignedRowId}
                      style={{
                        textTransform: 'capitalize',
                      }}
                      variant="outlined"
                      color="primary"
                      fullWidth
                      data-cy="remove-service-btn"
                    >
                      {i18n.t('common.remove')}
                    </Button>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={5}>
                <Box mb={5}>
                  <SelectableList
                    pagination={false}
                    dataCy="shared-services-list"
                    data={removeServices}
                    columns={assignedServicesColumns}
                    loading={loading}
                    onRowClicked={onAssignedRowClicked}
                    title={
                      sharingType === 'FFB'
                        ? i18n.t('vehicle.selected_partners')
                        : i18n.t('vehicle.selected_services')
                    }
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>
          {customErrors && (
            <Grid item xs={8}>
              <Typography align="center" color="error" gutterBottom component="div">
                <Box fontWeight="fontWeightBold" fontSize={14} my={2}>
                  {customErrors.message}
                </Box>
              </Typography>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

VehiclesGroupsServices.propTypes = {
  fleet: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
  customErrors: PropTypes.shape({
    message: PropTypes.string,
  }),
};

export { VehiclesGroupsServices };
